import React, { useEffect, useState } from 'react';
import { Download, X } from 'lucide-react';
import { Button } from './ui/button';

/**
 * PWA install helper.
 * - On Chromium (Android + desktop): shows an "Install app" toast when
 *   the browser fires `beforeinstallprompt`.
 * - On iOS Safari: shows a one-time hint on how to install
 *   (Share -> Add to Home Screen), since iOS doesn't fire the event.
 * - Never re-shown once the user dismisses or installs.
 */
const DISMISS_KEY = 'nexus-pwa-dismissed-v1';

const isIos = () => /iPhone|iPad|iPod/.test(navigator.userAgent);
const isStandalone = () =>
  window.matchMedia('(display-mode: standalone)').matches ||
  window.navigator.standalone === true;

export const PWAInstallPrompt = () => {
  const [deferred, setDeferred] = useState(null);
  const [visible, setVisible] = useState(false);
  const [showIosHint, setShowIosHint] = useState(false);

  useEffect(() => {
    if (isStandalone() || localStorage.getItem(DISMISS_KEY)) return;

    const onPrompt = (e) => {
      e.preventDefault();
      setDeferred(e);
      setVisible(true);
    };
    window.addEventListener('beforeinstallprompt', onPrompt);

    // iOS Safari fallback (Safari has no beforeinstallprompt)
    if (isIos() && !isStandalone()) {
      const t = setTimeout(() => setShowIosHint(true), 2500);
      return () => {
        clearTimeout(t);
        window.removeEventListener('beforeinstallprompt', onPrompt);
      };
    }

    const onInstalled = () => {
      setVisible(false);
      localStorage.setItem(DISMISS_KEY, '1');
    };
    window.addEventListener('appinstalled', onInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', onPrompt);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, []);

  const dismiss = () => {
    setVisible(false);
    setShowIosHint(false);
    localStorage.setItem(DISMISS_KEY, '1');
  };

  const doInstall = async () => {
    if (!deferred) return;
    deferred.prompt();
    await deferred.userChoice;
    setDeferred(null);
    setVisible(false);
    localStorage.setItem(DISMISS_KEY, '1');
  };

  // Chromium: install banner
  if (visible) {
    return (
      <div
        className="fixed z-50 left-3 right-3 lg:left-auto lg:right-6 lg:bottom-6 lg:w-80 bottom-[76px] lg:bottom-6 bg-slate-900 text-white rounded-xl shadow-2xl p-4 flex items-start gap-3 animate-in fade-in slide-in-from-bottom-4"
        data-testid="pwa-install-banner"
      >
        <div className="w-10 h-10 rounded-lg bg-white/10 flex items-center justify-center flex-shrink-0">
          <Download className="h-5 w-5" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-sm">Install Nexus ERP</p>
          <p className="text-xs text-white/70 mt-0.5">
            Add to your home screen for a faster, app-like experience.
          </p>
          <div className="flex gap-2 mt-3">
            <Button
              size="sm"
              className="bg-white text-slate-900 hover:bg-white/90 h-8"
              onClick={doInstall}
              data-testid="pwa-install-button"
            >
              Install
            </Button>
            <Button
              size="sm"
              variant="ghost"
              className="text-white/80 hover:text-white hover:bg-white/10 h-8"
              onClick={dismiss}
              data-testid="pwa-install-dismiss"
            >
              Not now
            </Button>
          </div>
        </div>
        <button
          onClick={dismiss}
          className="text-white/60 hover:text-white flex-shrink-0"
          aria-label="Dismiss"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    );
  }

  // iOS Safari: instructions banner
  if (showIosHint) {
    return (
      <div
        className="fixed z-50 left-3 right-3 bottom-[76px] bg-slate-900 text-white rounded-xl shadow-2xl p-4 flex items-start gap-3 animate-in fade-in slide-in-from-bottom-4"
        data-testid="pwa-ios-hint"
      >
        <div className="w-10 h-10 rounded-lg bg-white/10 flex items-center justify-center flex-shrink-0">
          <Download className="h-5 w-5" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-sm">Install Nexus ERP on iPhone</p>
          <p className="text-xs text-white/70 mt-0.5">
            Tap <span className="font-semibold">Share</span> → <span className="font-semibold">Add to Home Screen</span>.
          </p>
        </div>
        <button
          onClick={dismiss}
          className="text-white/60 hover:text-white flex-shrink-0"
          aria-label="Dismiss"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    );
  }

  return null;
};

export default PWAInstallPrompt;
